import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, ChevronDown, ChevronLeft,
  Bot, User, AlertCircle, Zap, MessageCircle,
} from 'lucide-react';
import api from '../../api/client';
import { useWhatsAppMessages } from '../../hooks/useWhatsAppMessages';
import MessageBubble from './MessageBubble';
import { contactDisplayName, contactNumberLine } from '../../utils/contact-display';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  '#8B5CF6', '#3B82F6', '#EC4899', '#F97316', '#14B8A6',
  '#EF4444', '#EAB308', '#6366F1', '#10B981', '#F59E0B',
];

function getAvatarColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ChatWindow({ conversation, onClose }) {
  const [input, setInput] = useState('');
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [aiActive, setAiActive] = useState(() => !!conversation?.agentId);
  const [takingOver, setTakingOver] = useState(false);

  const textareaRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const { messages, loading, error, sendReply, sending, messagesEndRef } =
    useWhatsAppMessages(conversation?._id);

  const name = contactDisplayName(conversation);
  const numberLine = contactNumberLine(conversation);
  const initials = name.substring(0, 2).toUpperCase() || '??';
  const avatarColor = getAvatarColor(name);

  useEffect(() => {
    setAiActive(!!conversation?.agentId);
  }, [conversation?._id, conversation?.agentId]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const sh = el.scrollHeight;
    el.style.height =
      (typeof sh === 'number' && !isNaN(sh) ? Math.min(sh, 120) : 42) + 'px';
  }, [input]);

  useEffect(() => {
    setInput('');
  }, [conversation?._id]);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 100);
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesEndRef]);

  const handleTakeOver = useCallback(async () => {
    if (takingOver || !conversation?._id) return;
    setTakingOver(true);
    const user = getCurrentUser();
    const userId = user.id || user._id;
    try {
      if (userId) {
        await api.patch(`/conversations/${conversation._id}/assign`, { userId });
      }
    } catch (err) {
      console.error('Take over error:', err);
    } finally {
      setAiActive(false);
      setTakingOver(false);
    }
  }, [conversation?._id, takingOver]);

  const handleReleaseToAI = useCallback(async () => {
    try {
      await api.patch(`/conversations/${conversation._id}/status`, { status: 'active' });
    } catch (err) {
      console.error('Release to AI error:', err);
    } finally {
      setAiActive(true);
    }
  }, [conversation?._id]);

  const handleSend = useCallback(async () => {
    const content = input.trim();
    if (!content || sending) return;
    const ok = await sendReply(content);
    if (ok) setInput('');
  }, [input, sending, sendReply]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-primary-dark via-primary-dark to-[#0d1117]">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center px-6"
        >
          <div className="relative w-24 h-24 mx-auto mb-5">
            <div className="absolute inset-0 rounded-3xl bg-accent-green/20 blur-2xl" />
            <div className="relative w-24 h-24 rounded-3xl bg-gradient-to-br from-primary-card to-primary-card/60 border border-primary-sidebar/60 flex items-center justify-center">
              <MessageCircle size={40} className="text-accent-green" />
            </div>
          </div>
          <p className="text-white font-semibold text-lg mb-1">Vos conversations WhatsApp</p>
          <p className="text-text-gray text-sm">
            Sélectionnez une conversation pour commencer
          </p>
        </motion.div>
      </div>
    );
  }

  const isOnline = conversation.status === 'active';

  return (
    <div className="flex-1 flex flex-col bg-gradient-to-br from-primary-dark via-primary-dark to-[#0d1117] overflow-hidden">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div
        className="border-b border-primary-sidebar/60 bg-primary-card/80 backdrop-blur-xl flex items-center gap-2 flex-shrink-0"
        style={{ padding: '10px 16px' }}
      >
        {/* Back button (mobile only) — pl-14 md:pl-0 on the row handles hamburger clearance */}
        {onClose && (
          <motion.button
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={onClose}
            className="md:hidden flex-shrink-0 p-2 rounded-xl text-text-gray hover:text-white hover:bg-primary-dark transition-colors"
            style={{ marginLeft: '52px' }}
          >
            <ChevronLeft size={20} />
          </motion.button>
        )}

        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-white text-sm shadow-md"
            style={{ background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}99)` }}
          >
            {initials}
          </div>
          {isOnline && (
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-primary-card" />
          )}
        </div>

        {/* Contact info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-semibold text-sm leading-tight truncate">{name}</p>
            {/* Mode pill — always visible, replaces the full-width banner */}
            <span
              className={`hidden sm:inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                aiActive
                  ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                  : 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
              }`}
            >
              {aiActive ? <Zap size={9} /> : <User size={9} />}
              {aiActive ? 'IA active' : 'Opérateur'}
            </span>
          </div>
          <p className={`text-[11px] leading-tight truncate ${numberLine ? 'text-text-gray' : 'text-text-gray/40 italic'}`}>
            {numberLine || 'Numéro non disponible'}
          </p>
        </div>

        {/* AI / Operator toggle */}
        <div className="flex-shrink-0">
          {aiActive ? (
            <motion.button
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={handleTakeOver}
              disabled={takingOver}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25 transition-colors text-xs font-semibold disabled:opacity-50"
              title="Prendre en main — l'IA arrête de répondre"
            >
              {takingOver ? (
                <div className="animate-spin rounded-full h-3 w-3 border border-emerald-400 border-t-transparent" />
              ) : (
                <User size={12} />
              )}
              <span className="hidden sm:inline">
                {takingOver ? 'En cours…' : 'Prendre en main'}
              </span>
            </motion.button>
          ) : (
            <motion.button
              whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              onClick={handleReleaseToAI}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-500/15 border border-blue-500/40 text-blue-400 hover:bg-blue-500/25 transition-colors text-xs font-semibold"
              title="Rendre à l'IA — l'IA reprend les réponses"
            >
              <Bot size={12} />
              <span className="hidden sm:inline">Rendre à l'IA</span>
            </motion.button>
          )}
        </div>
      </div>

      {/* ── Messages area ────────────────────────────────────────────────── */}
      <div className="flex-1 relative overflow-hidden">
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="h-full overflow-y-auto py-2"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.025) 1px, transparent 0)',
            backgroundSize: '22px 22px',
          }}
        >
          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-green" />
            </div>
          )}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <AlertCircle className="text-red-400" size={32} />
              <p className="text-text-gray text-sm">{error}</p>
            </div>
          )}
          {!loading && !error && messages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <p className="text-text-gray text-sm">
                Aucun message — démarrez la conversation
              </p>
            </div>
          )}
          <AnimatePresence initial={false} key={conversation._id}>
            {!loading && !error && messages.map((msg, i) => (
              <motion.div
                key={msg._id || msg.id || `idx-${i}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: msg._isOptimistic ? 0.65 : 1, y: 0 }}
                transition={{ duration: 0.18 }}
              >
                <MessageBubble
                  message={msg}
                  prevMessage={messages[i - 1]}
                  contactName={name}
                  isPending={msg._isOptimistic === true}
                />
              </motion.div>
            ))}
          </AnimatePresence>
          <div ref={messagesEndRef} className="h-2" />
        </div>

        <AnimatePresence>
          {showScrollBtn && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              onClick={scrollToBottom}
              className="absolute bottom-4 right-4 bg-primary-card border border-primary-sidebar text-white p-2 rounded-full shadow-lg hover:bg-primary-sidebar transition-colors z-10"
            >
              <ChevronDown size={16} />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* ── Input bar ─────────────────────────────────────────────────────── */}
      <div
        className="px-3 pt-2.5 pb-3 bg-primary-card flex-shrink-0"
        style={{
          borderTop: `1.5px solid ${aiActive ? 'rgba(16,185,129,0.3)' : 'rgba(59,130,246,0.45)'}`,
        }}
      >
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              aiActive
                ? "Message d'urgence (bypass IA)…"
                : "Votre réponse… (Entrée pour envoyer)"
            }
            disabled={sending}
            rows={1}
            className="flex-1 bg-primary-dark text-white text-sm px-4 py-2.5 rounded-2xl focus:outline-none transition-all disabled:opacity-50 placeholder-text-gray"
            style={{
              resize: 'none',
              overflow: 'hidden',
              minHeight: '42px',
              maxHeight: '120px',
              boxShadow: `inset 0 0 0 1.5px ${aiActive ? 'rgba(16,185,129,0.3)' : 'rgba(59,130,246,0.5)'}`,
            }}
          />
          <motion.button
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={handleSend}
            disabled={sending || !input.trim()}
            className={`flex items-center justify-center gap-1.5 text-white px-3.5 py-2.5 rounded-2xl disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg transition-all flex-shrink-0 ${
              aiActive
                ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 hover:shadow-emerald-500/30'
                : 'bg-gradient-to-br from-blue-500 to-blue-600 hover:shadow-blue-500/30'
            }`}
            title="Envoyer"
          >
            {sending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Send size={15} />
            )}
          </motion.button>
        </div>
      </div>
    </div>
  );
}
